import { useCallback, useEffect, useState } from 'preact/hooks';
import { offers } from 'signals/offers';
import { cn } from 'utils/cn.tsx';
import { localize } from 'i18n/localize.ts';
import { Icon } from 'components/atoms/Icon.tsx';
import type { OfferData } from 'service/offer/model.ts';
import { useSignals } from 'context/SignalsContext.tsx';
import type { SendSaveOfferAnalyticsAdditionalData } from 'lib/dot/generic/saveOffer.ts';
import { DOTHelper } from 'lib/dot/DOTHelper.ts';
import { PageType } from 'lib/dot/enum/pageType.ts';
import { phraseCategories as globalPhraseCategories } from 'components/filters/categoryList/signals';
import { displayCategories as globalDisplayCategories } from 'signals/category';
import { favoriteOffers, onFavoriteClick } from 'signals/favoriteOffers';

const $t = localize();

interface Props {
	className?: string;
	dataTest?: string;
	iconSize?: number;
	offer: OfferData;
	analyticsPosition?: string;
}

export function FavoriteButton({ className, iconSize = 20, offer, analyticsPosition, dataTest }: Props) {
	const { phraseCategories = globalPhraseCategories, displayCategories = globalDisplayCategories } = useSignals([]);
	const [isFavorite, setIsFavorite] = useState(false);

	//  Favorite offers are fetched on client side only, so the value is retrieved on client only to prevent rendering mismatch.
	useEffect(() => {
		setIsFavorite(!!favoriteOffers.peek()?.[offer.id]);
	}, [favoriteOffers.value]);

	const handleOfferClick = useCallback(
		(event: MouseEvent) => {
			const additionalData: SendSaveOfferAnalyticsAdditionalData = {
				...(analyticsPosition && { advertPosition: analyticsPosition }),
			};
			if (DOTHelper.getStore().pageType === PageType.SEARCH_RESULTS) {
				const offersValue = offers.peek();
				const itemCount =
					'response' in offersValue && offersValue.response
						? offersValue.response.pagination.total
						: offersValue.pagination.total;
				const categoryCount = phraseCategories.peek()?.length ?? displayCategories.peek().length ?? 0;
				additionalData['itemCount'] = itemCount;
				additionalData['categoryCount'] = categoryCount;
			}
			onFavoriteClick(event, offer, additionalData);
		},
		[offer, analyticsPosition],
	);

	return (
		<button
			data-test={dataTest}
			onClick={handleOfferClick}
			title={isFavorite ? $t.offer.unfavorite : $t.offer.favorite}
			class={cn(
				'flex items-center justify-center w-8 h-8 border rounded-lg group text-white',
				isFavorite
					? 'bg-red icon-fill deviceWithHover:hover:icon-unfill'
					: 'bg-dark-blue-50 deviceWithHover:hover:bg-dark-blue deviceWithHover:hover:icon-fill',
				className,
			)}
		>
			<Icon type="bookmark_add" size={iconSize} />
		</button>
	);
}
